import canvasSize from 'canvas-size';
import type { Rect } from '@view-model/models/common/basic/Rect';
import type { DrawSize } from './SVGToImageDefinitions';
import { FontStyle, ZoomScale } from './SVGToImageDefinitions';

type RenderOptions = {
  // 出力画像の長辺の最大ピクセル数。描画サイズがこれを超える場合のみ縮小する（拡大はしない）
  maxDimension?: number;
};

export type RenderedImage = {
  // PNG形式のdata URL（`data:image/png;base64,` から始まる）
  dataUrl: string;
  // 出力画像の実ピクセル幅（整数）
  width: number;
  // 出力画像の実ピクセル高（整数）
  height: number;
};

/**
 * 描画サイズの長辺が maxDimension を超えないように縮小する（アスペクト比は維持、拡大はしない）.
 *
 * @param drawSize 描画サイズ
 * @param maxDimension 長辺の最大ピクセル数（未指定時は補正しない）
 * @returns 補正後の描画サイズ
 */
export function applyMaxDimension(drawSize: DrawSize, maxDimension?: number): DrawSize {
  if (maxDimension === undefined) return drawSize;

  const longSide = Math.max(drawSize.width, drawSize.height);
  if (longSide <= maxDimension) return drawSize;

  const scale = maxDimension / longSide;
  return {
    width: drawSize.width * scale,
    height: drawSize.height * scale,
  };
}

/**
 * image要素が参照する画像URLをdata URLに差し替える.
 *
 * setAttribute('xlink:href', ...) は要素が名前空間付きの xlink:href を持たない場合に
 * 名前空間宣言のない属性を作ってしまい、XMLSerializerの出力が不正なXMLになって
 * Imageのロード（rasterize）が失敗する。SVG2のhref（xlink:hrefより優先される）に
 * 統一し、元から付いていたxlink:hrefは残さず取り除く。
 *
 * @param image image要素
 * @param dataUrl 差し替え先のdata URL
 */
const XLINK_NAMESPACE_URI = 'http://www.w3.org/1999/xlink';

export function replaceSVGImageHref(image: SVGImageElement, dataUrl: string): void {
  image.setAttribute('href', dataUrl);
  // 名前空間宣言のない壊れた属性(旧実装のsetAttributeが作っていたもの)は修飾名一致で、
  // 名前空間付きの属性(Reactのxlink:href)はプレフィックスに依存せず、それぞれ取り除く
  image.removeAttribute('xlink:href');
  image.removeAttributeNS(XLINK_NAMESPACE_URI, 'href');
}

export class SVGToImage {
  /**
   * 実行中のブラウザがサポートする最大サイズから実際にCanvasに描画するサイズを算出する.
   * （なるべく高画質の画像にするため）
   *
   * @param drawSize 描画サイズ（補正前）
   * @param test ブラウザサポートしているサイズかをチェックする（OKの場合、補正しない）
   * @returns （実際の）描画サイズ
   */
  private static async adjustDrawSize(drawSize: DrawSize, test: boolean): Promise<DrawSize> {
    const { width, height } = await canvasSize.maxArea({ usePromise: true });
    if (test && drawSize.width <= width && drawSize.height <= height) {
      // 補正しない
      return drawSize;
    }

    const scaleW = width / drawSize.width;
    const scaleH = height / drawSize.height;

    const scale = Math.min(scaleW, scaleH);

    const adjustedDrawSize: DrawSize = {
      width: drawSize.width * scale,
      height: drawSize.height * scale,
    };

    return adjustedDrawSize;
  }

  /**
   * URLで参照される画像を読み込んで、そのデータをbase64形式にする.
   *
   * @param imageUrl 画像URL
   * @returns base64形式の画像データ文字列
   */
  private static async getBase64ImageFromUrl(imageUrl: string): Promise<string | null> {
    let blob: Blob | null = null;

    try {
      const response = imageUrl.includes('/view-model-assets')
        ? await fetch(imageUrl)
        : await fetch(`/fetch-image-for-export?imageUrl=${encodeURIComponent(imageUrl)}`);

      if (response.redirected) {
        // 画像URLがリダイレクトを経由する場合
        const redirectRes = await fetch(response.url);
        blob = await redirectRes.blob();
      } else {
        blob = await response.blob();
      }
    } catch (err) {
      console.error('Error fetching image:', err);
      return null;
    }

    return new Promise<unknown>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = (e) => reject(e);
      if (blob === null) {
        reject('blob is null.');
        return;
      }
      reader.readAsDataURL(blob);
    })
      .then((base64Image: unknown) => {
        return base64Image as string;
      })
      .catch((e: unknown) => {
        console.error(e);
        return null;
      });
  }

  /**
   * URL参照している画像データを事前にfetchしておく
   *
   * @param cloned （画像出力用に複製した）SVG要素
   */
  private static async reapplyImages(cloned: SVGSVGElement): Promise<void> {
    // svg中にあるimgタグ
    const imgs = cloned.querySelectorAll('img[src]:not([src=""])') as NodeListOf<HTMLImageElement>;

    await Promise.all(
      Array.from(imgs).map(async (img) => {
        if (!img.getAttribute('draggable')) {
          // 同じurlが設定されているimgタグが２つ存在するが、描画対象になっている方（draggable属性がない方）
          // にのみ画像データを設定する
          const base64Image = await this.getBase64ImageFromUrl(img.src);
          if (base64Image) {
            img.src = base64Image;
          }
        }
      })
    );

    // svg中にあるimageタグ
    const images = cloned.querySelectorAll('image') as NodeListOf<SVGImageElement>;

    await Promise.all(
      Array.from(images).map(async (image) => {
        const base64Image = await this.getBase64ImageFromUrl(image.href.baseVal);
        if (base64Image) {
          replaceSVGImageHref(image, base64Image);
        }
      })
    );
  }

  private static createMergedStyles(): string {
    const locationURL = new URL(location.href);

    const sheets = document.styleSheets;
    const list = Array.from(sheets).flatMap((sheet) => {
      if (sheet.href !== null) {
        const sheetURL = new URL(sheet.href);
        if (locationURL.origin !== sheetURL.origin) {
          // 別ドメインのファイルはCORSエラーが発生するので読み込まない
          return [];
        }
      }

      return Array.from(sheet.cssRules).map((cssRule) => cssRule.cssText);
    });

    // フォントのスタイルは先頭に置かないとうまく適用されない
    list.unshift(FontStyle);

    return list.join(`\n`);
  }

  private static reapplyStyles(cloned: SVGSVGElement): void {
    // foreignObjectでラップしたstyleタグを作成
    const styleElem = document.createElement('style');
    styleElem.innerHTML = this.createMergedStyles();
    const foreignObject = document.createElement('foreignObject');
    foreignObject.appendChild(styleElem);

    // styleは先頭に挿入（最後に挿入すると適用順序の影響で見た目が変わってしまう）
    cloned.prepend(foreignObject);
  }

  private static serialize(cloned: SVGSVGElement): string {
    const svgData = new XMLSerializer().serializeToString(cloned);
    cloned.remove();

    return btoa(unescape(encodeURIComponent(svgData)));
  }

  /**
   * 出力対象のsvg全体をbase64化する.
   *
   * @param svg SVG要素
   * @returns base64形式の画像データ文字列
   */
  private static async createBase64SVG(svg: SVGSVGElement) {
    // 表示中のSVGに直接変更を加えられないので、cloneしたものに対して変更を加える
    const cloned = svg.cloneNode(true) as SVGSVGElement;
    // 画面のズームに依らず、出力画像には文字を含める
    cloned.removeAttribute('data-lod');

    await this.reapplyImages(cloned);
    this.reapplyStyles(cloned);

    return this.serialize(cloned);
  }

  /**
   * base64化したSVGをcanvasでラスタライズして、PNG形式のdata URLと実寸法を返す.
   * （画像の読み込み・描画に失敗した場合は例外を投げる）
   *
   * @param base64SVG base64化したSVGデータ
   * @param drawSize canvasへの描画サイズ
   * @returns PNG形式のdata URLと実寸法
   */
  private static async rasterize(base64SVG: string, drawSize: DrawSize): Promise<RenderedImage> {
    const canvas = document.createElement('canvas');
    canvas.width = drawSize.width;
    canvas.height = drawSize.height;

    const base64SVGDataString = 'data:image/svg+xml;charset=utf-8;base64,' + base64SVG;

    try {
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = (e) => reject(e);
        image.src = base64SVGDataString;
      });

      const context = canvas.getContext('2d');
      context?.drawImage(img, 0, 0, drawSize.width, drawSize.height);
      const dataUrl = canvas.toDataURL('image/png');

      // canvasのwidth/heightは整数に丸められるため、出力画像の実寸法としてcanvasの値を返す
      return { dataUrl, width: canvas.width, height: canvas.height };
    } finally {
      canvas.height = 0;
      canvas.width = 0;
      canvas.remove();
    }
  }

  /**
   * 指定したViewのG要素（View本体・Viewタイトル部）から出力用のSVG要素と描画サイズ（補正前）を組み立てる.
   *
   * @param gView g要素（View本体）
   * @param gViewTitle g要素（Viewタイトル部）
   * @param viewRect View矩形情報
   * @returns 出力用のSVG要素と描画サイズ（補正前）
   */
  private static buildViewPartsSVG(
    gView: SVGGElement,
    gViewTitle: SVGGElement,
    viewRect: Rect
  ): { svg: SVGSVGElement; drawSize: DrawSize } {
    const x = 0;
    const y = 0;

    // g要素（Viewタイトル部）
    const clonedViewTitle = gViewTitle.cloneNode(true) as SVGGElement;
    const draggableGElement = clonedViewTitle.children.item(0) as SVGGElement;
    const titleRect = draggableGElement.children.item(0) as SVGRectElement;
    const titleHeight = -titleRect.y.baseVal.value;
    draggableGElement.transform.baseVal.getItem(0).setTranslate(x, y + titleHeight);

    // ViewPopupMenuを出力対象から除外
    clonedViewTitle.children.item(1)?.remove();

    // g要素（View本体）
    const width = viewRect.width;
    const heightView = viewRect.height;
    const height = heightView + titleHeight;

    const clonedView = gView.cloneNode(true) as SVGGElement;
    clonedView.transform.baseVal.getItem(0).setTranslate(x, y + titleHeight);

    // SVG要素を作成してg要素を配下に追加する
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');

    svg.setAttribute('width', `${width}`);
    svg.setAttribute('height', `${height}`);
    svg.setAttribute('viewBox', `${x} ${y} ${width} ${height}`);

    svg.appendChild(clonedView);
    svg.appendChild(clonedViewTitle);

    return { svg, drawSize: { width: width * ZoomScale, height: height * ZoomScale } };
  }

  /**
   * 指定したViewのG要素からSVG要素を作成し、PNG画像として出力する.
   *
   * @param gView g要素（View本体）
   * @param gViewTitle g要素（Viewタイトル部）
   * @param viewRect View矩形情報
   * @param fileName 出力画像ファイル名
   * @returns 成功時：true
   */
  static async exportFromViewParts(
    gView: SVGGElement,
    gViewTitle: SVGGElement,
    viewRect: Rect,
    fileName: string
  ): Promise<boolean> {
    const { svg, drawSize } = this.buildViewPartsSVG(gView, gViewTitle, viewRect);

    // サイズ補正
    const adjustedDrawSize = await this.adjustDrawSize(drawSize, true);

    return this.exportInternal(svg, fileName, adjustedDrawSize);
  }

  /**
   * 指定したViewのG要素からSVG要素を作成し、PNG形式のdata URLと実寸法を返す.
   * （ダウンロードは行わない。失敗した場合は例外を投げる）
   *
   * @param gView g要素（View本体）
   * @param gViewTitle g要素（Viewタイトル部）
   * @param viewRect View矩形情報
   * @param opts 出力オプション
   * @returns PNG形式のdata URLと実寸法
   */
  static async renderFromViewParts(
    gView: SVGGElement,
    gViewTitle: SVGGElement,
    viewRect: Rect,
    opts?: RenderOptions
  ): Promise<RenderedImage> {
    const { svg, drawSize } = this.buildViewPartsSVG(gView, gViewTitle, viewRect);

    // サイズ補正
    const adjustedDrawSize = applyMaxDimension(await this.adjustDrawSize(drawSize, true), opts?.maxDimension);

    return this.renderInternal(svg, adjustedDrawSize);
  }

  /**
   * 指定したSVG要素をPNG画像として出力する.
   *
   * @param svg SVG要素
   * @param fileName 出力画像ファイル名
   * @returns 成功時：true
   */
  static async export(svg: SVGSVGElement, fileName: string): Promise<boolean> {
    const drawSize = await this.adjustDrawSize(
      { width: svg.width.baseVal.value, height: svg.height.baseVal.value },
      false
    );
    return this.exportInternal(svg, fileName, drawSize);
  }

  /**
   * 指定したSVG要素をPNG画像化して、data URLと実寸法を返す.
   * （ダウンロードは行わない。失敗した場合は例外を投げる）
   *
   * @param svg SVG要素
   * @param opts 出力オプション
   * @returns PNG形式のdata URLと実寸法
   */
  static async render(svg: SVGSVGElement, opts?: RenderOptions): Promise<RenderedImage> {
    const drawSize = await this.adjustDrawSize(
      { width: svg.width.baseVal.value, height: svg.height.baseVal.value },
      false
    );
    return this.renderInternal(svg, applyMaxDimension(drawSize, opts?.maxDimension));
  }

  private static async renderInternal(svg: SVGSVGElement, drawSize: DrawSize): Promise<RenderedImage> {
    const base64SVG = await this.createBase64SVG(svg);
    return this.rasterize(base64SVG, drawSize);
  }

  private static async exportInternal(svg: SVGSVGElement, fileName: string, drawSize: DrawSize): Promise<boolean> {
    // createBase64SVGの失敗は従来通り例外として伝播させる（ラスタライズ以降の失敗のみfalseを返す）
    const base64SVG = await this.createBase64SVG(svg);

    try {
      const { dataUrl } = await this.rasterize(base64SVG, drawSize);

      const downloadLink = document.createElement('a');
      downloadLink.href = dataUrl;
      downloadLink.download = fileName;
      downloadLink.click();

      return true;
    } catch (e: unknown) {
      console.error(e);
      return false;
    }
  }
}
